import {
  alpha,
  Box,
  Checkbox,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Theme,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { SimpleTreeView, TreeItem, treeItemClasses } from '@mui/x-tree-view';
import AddBoxRoundedIcon from '@mui/icons-material/AddBoxRounded';
import IndeterminateCheckBoxRoundedIcon from '@mui/icons-material/IndeterminateCheckBoxRounded';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';
import { Iconify } from 'src/components/iconify';
import {
  PermissionItem,
  PermissionGroupNode,
  PermissionResourceNode,
  PermissionSectionNode,
  collectExpandableIds,
  filterPermissionSections,
  getResourceLabel,
  groupPermissionsByMenuSection,
} from '../../utils/permission-menu-groups';

// ----------------------------------------------------------------------

export type PermissionPickerProps = {
  /** Flat permission list — use `flattenPermissionGroups` for the grouped API payload. */
  permissions: PermissionItem[];
  selectedIds: string[];
  /** Receives the full next selection, so callers can post-process it. */
  onSelectedIdsChange: (nextIds: string[]) => void;
  disabled?: boolean;
  /**
   * Permissions the subject already holds through something else — a user's
   * roles, typically. They show as ticked but cannot be toggled here, because
   * removing them means editing the role, not this list.
   */
  lockedIds?: string[];
  /** Height of the scrollable tree area. */
  height?: number | string;
  /** Rendered on the right of an action row, e.g. a status chip. */
  renderActionSuffix?: (permission: PermissionItem) => ReactNode;
  emptyContent?: ReactNode;
};

export function PermissionPicker({
  permissions,
  selectedIds,
  onSelectedIdsChange,
  disabled = false,
  lockedIds,
  height = '60vh',
  renderActionSuffix,
  emptyContent,
}: PermissionPickerProps) {
  const translate = useTranslate();
  const [search, setSearch] = useState('');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [selectedOnly, setSelectedOnly] = useState(false);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const lockedSet = useMemo(() => new Set(lockedIds || []), [lockedIds]);
  /** Ticked here or granted elsewhere — both read as "the subject has it". */
  const isOn = (id: string) => selectedSet.has(id) || lockedSet.has(id);

  // Without a permission the checkboxes are disabled; MUI then drops the cursor
  // entirely, so surface a `not-allowed` cursor to make the read-only state obvious.
  const checkboxSx = {
    p: 0.5,
    pr: 1,
    '&.Mui-disabled': { cursor: 'not-allowed', pointerEvents: 'auto' },
  } as const;

  // Only the "selected only" view depends on the selection, so keep the full
  // tree out of that dependency — otherwise every checkbox click rebuilds all
  // ~140 resources for a view that does not use the selection at all.
  const allSections = useMemo(() => groupPermissionsByMenuSection(permissions), [permissions]);
  const selectedSections = useMemo(
    () =>
      selectedOnly
        ? groupPermissionsByMenuSection(
            permissions.filter((item) => selectedSet.has(item.id) || lockedSet.has(item.id))
          )
        : null,
    [permissions, selectedOnly, selectedSet, lockedSet]
  );
  const sections = selectedSections ?? allSections;

  const filteredSections = useMemo(
    () => filterPermissionSections(sections, search, translate),
    [sections, search, translate]
  );

  const isSearching = !!search.trim();
  const matchCount = useMemo(
    () => filteredSections.reduce((total, section) => total + section.actionIds.length, 0),
    [filteredSections]
  );

  // While searching (or in "selected only" mode) every match is expanded, so the
  // operator sees hits straight away instead of hunting through collapsed nodes.
  //
  // The mode matters more than the dependency list here. Inside a mode the tree
  // contents drive expansion, so switching roles or ticking a box re-expands
  // what is now on screen. Outside a mode we must NOT react to content changes:
  // ticking a checkbox rebuilds `filteredSections`, and collapsing on that would
  // shut the whole tree on every single click. So the collapse only fires on the
  // transition out of a mode, not on every render.
  const autoExpand = isSearching || selectedOnly;
  const wasAutoExpanding = useRef(false);

  useEffect(() => {
    if (autoExpand) {
      setExpandedItems(collectExpandableIds(filteredSections));
    } else if (wasAutoExpanding.current) {
      setExpandedItems([]);
    }
    wasAutoExpanding.current = autoExpand;
  }, [autoExpand, filteredSections]);

  const toggleIds = (ids: string[], checked: boolean) => {
    // Locked ones are not ours to grant or revoke, so a parent checkbox skips them.
    const own = ids.filter((id) => !lockedSet.has(id));
    onSelectedIdsChange(
      checked ? [...new Set([...selectedIds, ...own])] : selectedIds.filter((id) => !own.includes(id))
    );
  };

  const countSelected = (ids: string[]) => ids.filter(isOn).length;

  /** Matches the ticked boxes: locked grants show as on, so they count too. */
  const totalSelected = useMemo(
    () => new Set([...selectedIds, ...(lockedIds || [])]).size,
    [selectedIds, lockedIds]
  );

  const renderCount = (ids: string[]) => {
    const selected = countSelected(ids);
    if (!selected) return null;
    return (
      <Chip
        size="small"
        variant="soft"
        color="primary"
        label={`${selected}/${ids.length}`}
        sx={{ ml: 1, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
      />
    );
  };

  const renderResource = (node: PermissionResourceNode) => {
    const selected = countSelected(node.actionIds);
    const isChecked = node.actionIds.length > 0 && selected === node.actionIds.length;
    const isIndeterminate = selected > 0 && !isChecked;
    const menuLabel = getResourceLabel(node, translate);

    return (
      <PermissionTreeItem
        key={node.nodeId}
        itemId={node.nodeId}
        label={
          <Box display="flex" alignItems="center" minWidth={0}>
            <Checkbox
              size="small"
              onClick={(e) => e.stopPropagation()}
              // All-locked rows would look clickable but do nothing, since
              // `toggleIds` skips locked ids.
              disabled={disabled || node.actionIds.every((id) => lockedSet.has(id))}
              checked={isChecked}
              indeterminate={isIndeterminate}
              onChange={(e) => toggleIds(node.actionIds, e.target.checked)}
              sx={checkboxSx}
            />
            <Box display="flex" alignItems="baseline" gap={0.75} minWidth={0}>
                {menuLabel ? (
                  <Typography variant="body2" noWrap>
                    {menuLabel}
                  </Typography>
                ) : null}
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: 'text.secondary', fontFamily: 'monospace' }}
                >
                  {node.resource}
                </Typography>
              </Box>
            {renderCount(node.actionIds)}
          </Box>
        }
      >
        {node.actions.map((permission) => (
          <PermissionTreeItem
            key={permission.id}
            itemId={permission.id}
            label={
              <Box display="flex" alignItems="center" minWidth={0}>
                <Checkbox
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  disabled={disabled || lockedSet.has(permission.id)}
                  checked={isOn(permission.id)}
                  onChange={(e) => toggleIds([permission.id], e.target.checked)}
                  sx={checkboxSx}
                />
                <Typography variant="body2" noWrap>
                  {permission.action}
                </Typography>
                {renderActionSuffix?.(permission)}
              </Box>
            }
          />
        ))}
      </PermissionTreeItem>
    );
  };

  /** A grouping menu item — carries no permission of its own, only what is below it. */
  const renderGroup = (group: PermissionGroupNode) => {
    const selected = countSelected(group.actionIds);
    const isChecked = group.actionIds.length > 0 && selected === group.actionIds.length;
    const isIndeterminate = selected > 0 && !isChecked;

    return (
      <PermissionTreeItem
        key={group.nodeId}
        itemId={group.nodeId}
        label={
          <Box display="flex" alignItems="center" minWidth={0}>
            <Checkbox
              size="small"
              onClick={(e) => e.stopPropagation()}
              disabled={disabled || group.actionIds.every((id) => lockedSet.has(id))}
              checked={isChecked}
              indeterminate={isIndeterminate}
              onChange={(e) => toggleIds(group.actionIds, e.target.checked)}
              sx={checkboxSx}
            />
            <Typography variant="body2" noWrap sx={{ fontWeight: 'fontWeightSemiBold' }}>
              {translate(group.titleKey)}
            </Typography>
            {renderCount(group.actionIds)}
          </Box>
        }
      >
        {group.groups.map(renderGroup)}
        {group.resources.map(renderResource)}
      </PermissionTreeItem>
    );
  };

  const renderSection = (section: PermissionSectionNode) => {
    const selected = countSelected(section.actionIds);
    const isChecked = section.actionIds.length > 0 && selected === section.actionIds.length;
    const isIndeterminate = selected > 0 && !isChecked;

    return (
      <PermissionTreeItem
        key={section.nodeId}
        itemId={section.nodeId}
        label={
          <Box display="flex" alignItems="center" minWidth={0}>
            <Checkbox
              size="small"
              onClick={(e) => e.stopPropagation()}
              disabled={disabled || section.actionIds.every((id) => lockedSet.has(id))}
              checked={isChecked}
              indeterminate={isIndeterminate}
              onChange={(e) => toggleIds(section.actionIds, e.target.checked)}
              sx={checkboxSx}
            />
            <Typography variant="subtitle2" noWrap>
              {translate(section.sectionKey)}
            </Typography>
            {renderCount(section.actionIds)}
          </Box>
        }
      >
        {section.groups.map(renderGroup)}
        {section.resources.map(renderResource)}
      </PermissionTreeItem>
    );
  };

  const allExpandableIds = useMemo(() => collectExpandableIds(filteredSections), [filteredSections]);
  const isAllExpanded =
    allExpandableIds.length > 0 && allExpandableIds.every((id) => expandedItems.includes(id));

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={translate('module.permissions.searchPlaceholder')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" width={18} sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <Iconify
                  icon="eva:close-fill"
                  width={18}
                  sx={{ color: 'text.disabled', cursor: 'pointer' }}
                  onClick={() => setSearch('')}
                />
              </InputAdornment>
            ) : null,
          }}
        />
        <Tooltip title={translate('module.permissions.showSelectedOnly')}>
          <Chip
            size="small"
            clickable
            color={selectedOnly ? 'primary' : 'default'}
            variant={selectedOnly ? 'filled' : 'outlined'}
            label={`${translate('module.permissions.selected')} ${totalSelected}`}
            onClick={() => setSelectedOnly((prev) => !prev)}
          />
        </Tooltip>
        <Tooltip
          title={translate(
            isAllExpanded ? 'module.permissions.collapseAll' : 'module.permissions.expandAll'
          )}
        >
          <Chip
            size="small"
            clickable
            variant="outlined"
            label={<Iconify icon={isAllExpanded ? 'eva:collapse-fill' : 'eva:expand-fill'} width={16} />}
            onClick={() => setExpandedItems(isAllExpanded ? [] : allExpandableIds)}
            sx={{ '& .MuiChip-label': { display: 'flex', px: 0.75 } }}
          />
        </Tooltip>
      </Stack>

      {isSearching ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {translate('module.permissions.searchResult', { count: matchCount })}
        </Typography>
      ) : null}

      {filteredSections.length > 0 ? (
        <SimpleTreeView
          disableSelection
          expandedItems={expandedItems}
          onExpandedItemsChange={(_, items) => setExpandedItems(items)}
          slots={{ expandIcon: ExpandIcon, collapseIcon: CollapseIcon, endIcon: EndIcon }}
          sx={{ overflowX: 'hidden', overflowY: 'auto', height, flexGrow: 1 }}
        >
          {filteredSections.map(renderSection)}
        </SimpleTreeView>
      ) : (
        <Box
          sx={{
            height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
          }}
        >
          {emptyContent ?? (
            <Typography variant="body2">
              {translate(isSearching ? 'module.permissions.noSearchResult' : 'ra.navigation.no_results')}
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
}

// ----------------------------------------------------------------------

const PermissionTreeItem = styled(TreeItem)(({ theme }: { theme: Theme }) => ({
  [`& .${treeItemClasses.content}`]: {
    padding: theme.spacing(0.5, 1),
    margin: theme.spacing(0.2, 0),
  },
  [`& .${treeItemClasses.groupTransition}`]: {
    marginLeft: 15,
    paddingLeft: 18,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
  },
}));

const ExpandIcon = (props: React.PropsWithoutRef<typeof AddBoxRoundedIcon>) => (
  <AddBoxRoundedIcon {...props} sx={{ opacity: 0.8 }} />
);
const CollapseIcon = (props: React.PropsWithoutRef<typeof IndeterminateCheckBoxRoundedIcon>) => (
  <IndeterminateCheckBoxRoundedIcon {...props} sx={{ opacity: 0.8 }} />
);
const EndIcon = (props: React.PropsWithoutRef<typeof Box>) => <Box {...props} sx={{ width: 0 }} />;

export default PermissionPicker;
